import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { useEmployees } from '../hooks/useEmployees';
import { useAI } from '../hooks/useAI';
import { typeGroupColor, workStyleLabel, getTypeGroup, GROUP_LABELS, TYPE_NAMES } from '../lib/constants';
import { api } from '../lib/api';
import type { Department, CSVImportResult, PersonalityType } from '../lib/types';

const PER_PAGE = 10;

export default function EmployeeList() {
  const navigate = useNavigate();
  const { employees, loading, error, fetchEmployees, createEmployee, deleteEmployee } = useEmployees();
  const { recommendWorkstyle, loading: aiLoading } = useAI();
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [csvResult, setCsvResult] = useState<CSVImportResult | null>(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [newEmp, setNewEmp] = useState({
    name: '', employee_code: '', department_id: '', position: '',
    job_description: '', personality_type: '', work_style: '', consent_given: false,
  });

  useEffect(() => {
    fetchEmployees(search ? { search } : undefined);
  }, [fetchEmployees, search]);

  useEffect(() => {
    api.departments.list().then(res => {
      setDepartments(res.departments as unknown as Department[]);
    }).catch(() => {});
  }, []);

  // Reset page when tab/search changes
  useEffect(() => { setPage(1); }, [activeTab, search]);

  // ─── Filter tabs ───
  const filterTabs = useMemo(() => {
    const tabs: { key: string; label: string }[] = [{ key: 'all', label: '全員' }];
    // Type tabs: only types that exist
    const typeSet = new Set(employees.map(e => e.personality_type).filter(Boolean));
    typeSet.forEach(t => tabs.push({ key: `type:${t}`, label: t! }));
    // Dept tabs
    departments.forEach(d => {
      if (employees.some(e => e.department_id === d.id)) {
        tabs.push({ key: `dept:${d.id}`, label: d.name });
      }
    });
    return tabs;
  }, [employees, departments]);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return employees;
    if (activeTab.startsWith('type:')) {
      const t = activeTab.replace('type:', '');
      return employees.filter(e => e.personality_type === t);
    }
    if (activeTab.startsWith('dept:')) {
      const d = activeTab.replace('dept:', '');
      return employees.filter(e => e.department_id === d);
    }
    return employees;
  }, [employees, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const selectedEmployee = selectedId ? employees.find(e => e.id === selectedId) : null;

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: Record<string, unknown> = { name: newEmp.name };
    if (newEmp.employee_code) data.employee_code = newEmp.employee_code;
    if (newEmp.department_id) data.department_id = newEmp.department_id;
    if (newEmp.position) data.position = newEmp.position;
    if (newEmp.job_description) data.job_description = newEmp.job_description;
    if (newEmp.personality_type) data.personality_type = newEmp.personality_type;
    if (newEmp.work_style) data.work_style = newEmp.work_style;
    data.consent_given = newEmp.consent_given;
    const ok = await createEmployee(data);
    if (ok) {
      setShowAddModal(false);
      setNewEmp({ name: '', employee_code: '', department_id: '', position: '', job_description: '', personality_type: '', work_style: '', consent_given: false });
    }
  };

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvLoading(true);
    try {
      const result = await api.employees.importCSV(file);
      setCsvResult(result as unknown as CSVImportResult);
      setShowCSVModal(true);
      fetchEmployees();
    } catch (err) {
      setCsvResult({ total: 0, created: 0, updated: 0, error_count: 1, errors: [{ row: 0, message: err instanceof Error ? err.message : 'CSVインポートに失敗しました' }] });
      setShowCSVModal(true);
    } finally {
      setCsvLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    await deleteEmployee(id);
    setConfirmDeleteId(null);
    if (selectedId === id) setSelectedId(null);
  };

  const handleAIAnalyze = async (e: React.MouseEvent, empId: string) => {
    e.stopPropagation();
    setAnalyzingId(empId);
    const result = await recommendWorkstyle(empId, true);
    setAnalyzingId(null);
    if (result) {
      navigate(`/employees/${empId}`);
    }
  };

  const formatDate = (d: string) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}.${(dt.getMonth()+1).toString().padStart(2,'0')}.${dt.getDate().toString().padStart(2,'0')}`;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
        <h2 className="text-[22px] font-bold text-text-main">社員一覧</h2>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleCSVImport} />
          <Button small onClick={() => fileInputRef.current?.click()} disabled={csvLoading}>
            {csvLoading ? 'インポート中...' : 'CSV'}
          </Button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary hover:bg-primary-hover text-white text-[13px] font-medium transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            社員追加
          </button>
        </div>
      </div>

      {/* Desktop: Main content with optional sidebar */}
      <div className="flex gap-4">
        {/* Left: Table area */}
        <div className="flex-1 min-w-0">
          {/* Tabs + Search */}
          <Card className="!p-0 mb-4">
            <div className="px-3 pt-3">
              {/* Tabs - horizontal scroll on mobile */}
              <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                {filterTabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium whitespace-nowrap transition-colors shrink-0 ${
                      activeTab === tab.key
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-sub hover:bg-surface-hover'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-3 pb-3 pt-2 flex gap-2">
              <div className="flex-1 relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="名前・タイプで検索"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-bg text-text-main text-[13px] outline-none focus:border-primary transition-colors"
                />
              </div>
            </div>
          </Card>

          {error && <div className="text-xs text-accent bg-caution-bg rounded-lg p-3 mb-4">{error}</div>}

          {/* Mobile: Card List */}
          <div className="lg:hidden space-y-2">
            {loading && <div className="text-center py-8 text-text-muted text-sm">読み込み中...</div>}
            {!loading && paginated.length === 0 && <div className="text-center py-8 text-text-muted text-sm">社員が見つかりません</div>}
            {paginated.map(emp => {
              const ws = workStyleLabel(emp.work_style);
              const tc = typeGroupColor(emp.personality_type);
              const initial = emp.name.charAt(0);
              return (
                <div
                  key={emp.id}
                  onClick={() => navigate(`/employees/${emp.id}`)}
                  className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-border-active cursor-pointer transition-colors"
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                    style={{ background: `${tc}20`, color: tc }}
                  >
                    {initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-text-main truncate">{emp.name}</div>
                    <div className="text-[11px] text-text-sub truncate">{emp.department_name || '-'}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {emp.personality_type ? (
                      <Badge bg={`${tc}22`} color={tc} className="!text-[11px]">{emp.personality_type}</Badge>
                    ) : (
                      <span className="text-[10px] text-text-muted">未入力</span>
                    )}
                    <span className={`text-[10px] ${ws.colorClass} px-1.5 py-0.5 rounded`}>{ws.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Table */}
          <Card className="!p-0 overflow-hidden hidden lg:block">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="bg-surface">
                  {['名前', '部署', 'タイプ', '働き方', '登録日', 'アクション'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-text-sub font-semibold text-[11px] uppercase tracking-wider border-b border-border whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-8 text-text-muted text-sm">読み込み中...</td></tr>
                )}
                {!loading && paginated.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-text-muted text-sm">社員が見つかりません</td></tr>
                )}
                {paginated.map(emp => {
                  const ws = workStyleLabel(emp.work_style);
                  const tc = typeGroupColor(emp.personality_type);
                  const initial = emp.name.charAt(0);
                  const isSelected = selectedId === emp.id;
                  return (
                    <tr
                      key={emp.id}
                      onClick={() => setSelectedId(emp.id)}
                      onDoubleClick={() => navigate(`/employees/${emp.id}`)}
                      className={`border-b border-border cursor-pointer transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-surface-hover'}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{ background: `${tc}20`, color: tc }}
                          >
                            {initial}
                          </div>
                          <span className="text-text-main font-semibold">{emp.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {emp.department_name ? (
                          <Badge bg="rgba(74,124,255,0.1)" color="#4A7CFF" className="!text-[11px]">{emp.department_name}</Badge>
                        ) : (
                          <span className="text-text-muted text-[11px]">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {emp.personality_type ? (
                          <Badge bg={`${tc}22`} color={tc}>{emp.personality_type}</Badge>
                        ) : (
                          <span className="text-text-muted text-[11px]">未入力</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge className={ws.colorClass}>{ws.label}</Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-text-sub text-[12px]">
                        {formatDate(emp.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {emp.personality_type && emp.consent_given ? (
                            <button
                              onClick={(e) => handleAIAnalyze(e, emp.id)}
                              disabled={aiLoading && analyzingId === emp.id}
                              className="p-1.5 rounded hover:bg-primary/10 text-primary transition-colors disabled:opacity-50"
                              title="AI分析"
                            >
                              {analyzingId === emp.id ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                              )}
                            </button>
                          ) : (
                            <span className="p-1.5 text-text-muted opacity-30" title="タイプ入力・同意が必要">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                            </span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/employees/${emp.id}`); }}
                            className="p-1.5 rounded hover:bg-surface-hover text-text-sub hover:text-primary transition-colors"
                            title="詳細"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(emp.id); }}
                            className="p-1.5 rounded hover:bg-surface-hover text-text-sub hover:text-accent transition-colors"
                            title="削除"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-[12px] text-text-sub hover:text-text-main disabled:opacity-30 transition-colors"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-md text-[12px] font-medium transition-colors ${
                    page === p ? 'bg-primary text-white' : 'text-text-sub hover:bg-surface-hover'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-[12px] text-text-sub hover:text-text-main disabled:opacity-30 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Right: Side Preview (Desktop only) */}
        {selectedEmployee && (
          <div className="hidden lg:block w-72 shrink-0">
            <Card className="sticky top-4">
              {/* Avatar */}
              <div className="flex flex-col items-center text-center mb-4">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold mb-3"
                  style={{
                    background: `${typeGroupColor(selectedEmployee.personality_type)}20`,
                    color: typeGroupColor(selectedEmployee.personality_type),
                    border: `3px solid ${typeGroupColor(selectedEmployee.personality_type)}40`,
                  }}
                >
                  {selectedEmployee.name.charAt(0)}
                </div>
                <div className="text-base font-bold text-text-main">{selectedEmployee.name}</div>
                <div className="text-[12px] text-text-sub mt-0.5">{selectedEmployee.department_name || '-'}</div>

                {selectedEmployee.personality_type && (
                  <div className="mt-3 flex flex-col items-center gap-1">
                    <span className="text-[11px] text-text-muted">性格タイプ</span>
                    <Badge
                      bg={`${typeGroupColor(selectedEmployee.personality_type)}22`}
                      color={typeGroupColor(selectedEmployee.personality_type)}
                      className="!text-sm !font-bold !px-4 !py-1"
                    >
                      {selectedEmployee.personality_type}
                    </Badge>
                    <span className="text-[10px] text-text-muted">
                      {TYPE_NAMES[selectedEmployee.personality_type as PersonalityType] || ''} ({GROUP_LABELS[getTypeGroup(selectedEmployee.personality_type)!] || ''})
                    </span>
                  </div>
                )}
              </div>

              {/* Type Radar - simplified 4-axis visual */}
              {selectedEmployee.personality_type && (
                <div className="mb-4 pt-3 border-t border-border">
                  <div className="text-[11px] text-text-muted mb-2 text-center">タイプ指標</div>
                  <div className="space-y-2">
                    {[
                      { left: 'E (外向)', right: 'I (内向)', isLeft: selectedEmployee.personality_type[0] === 'E' },
                      { left: 'S (感覚)', right: 'N (直感)', isLeft: selectedEmployee.personality_type[1] === 'S' },
                      { left: 'T (思考)', right: 'F (感情)', isLeft: selectedEmployee.personality_type[2] === 'T' },
                      { left: 'J (判断)', right: 'P (知覚)', isLeft: selectedEmployee.personality_type[3] === 'J' },
                    ].map((axis, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[10px]">
                        <span className={`w-14 text-right ${axis.isLeft ? 'text-primary font-bold' : 'text-text-muted'}`}>{axis.left}</span>
                        <div className="flex-1 h-1.5 rounded bg-surface overflow-hidden">
                          <div
                            className="h-full rounded transition-all"
                            style={{
                              width: axis.isLeft ? '75%' : '25%',
                              background: typeGroupColor(selectedEmployee.personality_type),
                            }}
                          />
                        </div>
                        <span className={`w-14 ${!axis.isLeft ? 'text-primary font-bold' : 'text-text-muted'}`}>{axis.right}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick info */}
              <div className="space-y-2 pt-3 border-t border-border text-[12px]">
                <div className="flex justify-between">
                  <span className="text-text-muted">役職</span>
                  <span className="text-text-main">{selectedEmployee.position || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">働き方</span>
                  <Badge className={workStyleLabel(selectedEmployee.work_style).colorClass + ' !text-[11px]'}>
                    {workStyleLabel(selectedEmployee.work_style).label}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">同意</span>
                  <span className={selectedEmployee.consent_given ? 'text-success' : 'text-accent'}>
                    {selectedEmployee.consent_given ? '済み' : '未取得'}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                {selectedEmployee.personality_type && selectedEmployee.consent_given && (
                  <Button
                    small
                    onClick={() => {
                      setAnalyzingId(selectedEmployee.id);
                      recommendWorkstyle(selectedEmployee.id, true).then(() => {
                        setAnalyzingId(null);
                        navigate(`/employees/${selectedEmployee.id}`);
                      });
                    }}
                    disabled={aiLoading && analyzingId === selectedEmployee.id}
                    className="flex-1"
                  >
                    {analyzingId === selectedEmployee.id ? '分析中...' : 'AI分析'}
                  </Button>
                )}
                <Button
                  primary small
                  onClick={() => navigate(`/employees/${selectedEmployee.id}`)}
                  className="flex-1"
                >
                  詳細を見る
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} title="社員を削除">
        <p className="text-sm text-text-sub mb-4">この社員を削除しますか？この操作は元に戻せません。</p>
        <div className="flex justify-end gap-2">
          <Button onClick={() => setConfirmDeleteId(null)}>キャンセル</Button>
          <Button primary onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}>削除する</Button>
        </div>
      </Modal>

      {/* Add Employee Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="社員を追加">
        <form onSubmit={handleAddEmployee} className="space-y-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">氏名 *</label>
            <input required value={newEmp.name} onChange={e => setNewEmp(p => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">社員番号</label>
              <input value={newEmp.employee_code} onChange={e => setNewEmp(p => ({ ...p, employee_code: e.target.value }))} placeholder="EMP-001"
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">部署</label>
              <select value={newEmp.department_id} onChange={e => setNewEmp(p => ({ ...p, department_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary">
                <option value="">選択なし</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">役職</label>
              <input value={newEmp.position} onChange={e => setNewEmp(p => ({ ...p, position: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">16タイプ</label>
              <select value={newEmp.personality_type} onChange={e => setNewEmp(p => ({ ...p, personality_type: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary">
                <option value="">未入力</option>
                {['ENTJ','ENTP','ENFJ','ENFP','ESTJ','ESTP','ESFJ','ESFP','INTJ','INTP','INFJ','INFP','ISTJ','ISTP','ISFJ','ISFP'].map(t =>
                  <option key={t} value={t}>{t}</option>
                )}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">業務内容</label>
            <textarea value={newEmp.job_description} onChange={e => setNewEmp(p => ({ ...p, job_description: e.target.value }))}
              placeholder="例：営業企画、データ分析、顧客対応など"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary resize-none" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={newEmp.consent_given} onChange={e => setNewEmp(p => ({ ...p, consent_given: e.target.checked }))} className="rounded" />
            <label className="text-xs text-text-sub">AI分析への同意済み</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setShowAddModal(false)}>キャンセル</Button>
            <Button primary type="submit">登録</Button>
          </div>
        </form>
      </Modal>

      {/* CSV Result Modal */}
      <Modal open={showCSVModal} onClose={() => setShowCSVModal(false)} title="CSVインポート結果">
        {csvResult && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-surface rounded-lg">
                <div className="text-lg font-bold text-success">{csvResult.created}</div>
                <div className="text-[11px] text-text-sub">新規作成</div>
              </div>
              <div className="text-center p-3 bg-surface rounded-lg">
                <div className="text-lg font-bold text-primary">{csvResult.updated}</div>
                <div className="text-[11px] text-text-sub">更新</div>
              </div>
              <div className="text-center p-3 bg-surface rounded-lg">
                <div className="text-lg font-bold text-accent">{csvResult.error_count}</div>
                <div className="text-[11px] text-text-sub">エラー</div>
              </div>
            </div>
            {csvResult.errors.length > 0 && (
              <div className="bg-caution-bg rounded-lg p-3">
                <div className="text-xs font-semibold text-accent mb-2">エラー詳細</div>
                {csvResult.errors.map((e, i) => (
                  <div key={i} className="text-xs text-text-sub py-1">{e.row > 0 ? `行${e.row}: ` : ''}{e.message}</div>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button primary small onClick={() => setShowCSVModal(false)}>閉じる</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
